import { ActionButton } from "@via/components/action-button/action-button";
import { SkeletonLoader } from "@via/components/loading/loading";
import Modal from "@via/components/modal";
import { useGetCustomerBoundary } from "@via/pages/account/admin/admin.api.tsx";
import { useAttachOrganizationBoundary } from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { BoundaryKind, ListCustomerBoundaryResponseBodyItem } from "common";
import { type JSX } from "react";

/**
 * The organization-first attach picker. It offers only boundaries an attach could actually take:
 * rows with no LIVE attachment, of a kind this deployment permits. A row with a live attachment
 * would refuse server side ("already attached to an organization"), and a kind outside the
 * deployment's set refuses too, so neither is listed. A detached row still carries its last
 * attachment's id for provenance, so liveness is the id AND no revocation, not the id alone.
 */
const rowLabel = (row: ListCustomerBoundaryResponseBodyItem): string => {
  switch (row.cloudVendor) {
    case BoundaryKind.GCP:
      return `${row.gcpProjectId} (${row.gcpServiceRegion})`;
    case BoundaryKind.AWS:
      return `${row.awsAccountId} / ${row.awsJobQueue} (${row.awsRegion})`;
    default:
      // Rows are stored in a VARCHAR column, so an unexpected kind is representable. Naming the
      // id keeps the row identifiable without re-implementing the server's label derivation.
      return row.id;
  }
};

export default function AttachBoundaryModal({
  organizationId,
  permittedKinds,
  isOpen,
  onClose,
}: {
  organizationId: string;
  permittedKinds: BoundaryKind[];
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element {
  const { data, isLoading } = useGetCustomerBoundary(0, 100);
  const attachMutation = useAttachOrganizationBoundary();

  const candidates = (data?.data ?? []).filter(
    row =>
      (row.organizationCustomerBoundaryId === null || row.revokedAt !== null) &&
      permittedKinds.includes(row.cloudVendor)
  );

  return (
    <Modal
      open={isOpen}
      setOpen={open => {
        if (!open) onClose();
      }}
      title="Attach a customer boundary"
      cancelText="Close">
      <div className="py-2 text-sm">
        {isLoading ? (
          <SkeletonLoader count={2} />
        ) : candidates.length === 0 ? (
          <div className="text-gray-600">
            There is no unclaimed boundary of a kind this deployment permits.
            Provision one on the Customer Boundary tab first.
          </div>
        ) : (
          <ul
            data-testid="attach-boundary-candidates"
            className="divide-y divide-gray-200 rounded-md border border-gray-200">
            {candidates.map(row => (
              <li
                key={row.id}
                className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-gray-900">
                    {rowLabel(row)}
                  </div>
                  <div className="text-gray-600">{row.cloudVendor}</div>
                </div>
                <ActionButton
                  button="Attach"
                  id={`attach-boundary-${row.id}`}
                  showIcon={false}
                  disabled={attachMutation.isPending}
                  onClick={() =>
                    attachMutation.mutate(
                      { organizationId, customerBoundaryId: row.id },
                      { onSuccess: () => onClose() }
                    )
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
